// docs/116 CI4: Hearth Bones, the inn's dice game. The pure rules (`handValue`, `handKind`, `handName`,
// `bustChance`, `regularChoice`) and `createLocalDiceTable`, a local engine of the exact state machine the
// module's `dice.rs` / `inn.rs` run, for the `?dice=1` and `?village=1` harnesses and the tests. The
// clock and the dice are injected (`now`, `seed`), so a run is the same every time.
import { DICE_STAKES, type DiceActions, type DiceLast, type DiceSeatView, type DiceView } from './types';

export type Dice3 = [number, number, number];

// ---- the rules ----------------------------------------------------------------------------------
/** A skull (a 1) is worth nothing. */
export const SKULL = 1;
export const TURN_MS = 30_000;
export const PAUSE_MS = 2_500;
export const SETTLE_MS = 6_000;
export const REGULAR_FIRST_MS = 1_600;
export const MAX_PUSHES = 2;

export type HandKind = 'crowns' | 'triple' | 'hearth' | 'plain';

export function isTriple(d: readonly number[]): boolean { return (d[0] ?? 0) > 1 && d[0] === d[1] && d[1] === d[2]; }
export function isHearth(d: readonly number[]): boolean { const s = [...d].sort(); return s[0] === 4 && s[1] === 5 && s[2] === 6; }
export function isGallows(d: readonly number[]): boolean { return d[0] === SKULL && d[1] === SKULL && d[2] === SKULL; }

/** A hand's value: a triple n (n ≥ 2) 20+n (Three Crowns 26), the Hearth (4-5-6) 19, else the sum of faces above 1 (max 17). */
export function handValue(d: readonly number[]): number {
  if (isTriple(d)) return 20 + d[0]!;
  if (isHearth(d)) return 19;
  return d.reduce((s, f) => s + (f > SKULL ? f : 0), 0);
}

export function handKind(d: readonly number[]): HandKind {
  if (isTriple(d)) return d[0] === 6 ? 'crowns' : 'triple';
  if (isHearth(d)) return 'hearth';
  return 'plain';
}

const NUMBER_WORD = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'];
const PLURAL = ['', 'Ones', 'Twos', 'Threes', 'Fours', 'Fives', 'Sixes'];
export { NUMBER_WORD };

/** What a hand is called: "Three Crowns", "Three Fives", "the Hearth", "11"; or "Bust!" / "the Gallows" for those ends. */
export function handName(d: readonly number[], end?: 'bust' | 'gallows'): string {
  if (end === 'gallows' || (end === 'bust' && isGallows(d))) return 'the Gallows';
  if (end === 'bust') return 'Bust!';
  const k = handKind(d);
  if (k === 'crowns') return 'Three Crowns';
  if (k === 'triple') return `Three ${PLURAL[d[0]!]}`;
  if (k === 'hearth') return 'the Hearth';
  return String(handValue(d));
}

/** The chance that pushing `k` dice busts (any of them lands a skull): 1 − (5/6)^k. */
export function bustChance(k: number): number {
  return k <= 0 ? 0 : 1 - Math.pow(5 / 6, k);
}

export function popcount(mask: number): number { let n = 0; for (let i = 0; i < 3; i++) if (mask & (1 << i)) n++; return n; }

/**
 * What a regular does with these dice (the hand thrown): 0 = hold, else the mask of dice to push.
 * By temper (0 cautious, 1 steady, 2 bold): hold a triple or a Hearth; else push the skulls and low dice
 * (≤ 2 + temper), or the odd die out of a high pair when bold, if the score is below the best stood
 * score at the table (min 10) or below 11 + 2·temper, and only when that pushes at most 1 + temper dice.
 */
export function regularChoice(dice: Dice3, pushes: number, temper: number, bestStood: number): number {
  if (pushes <= 0) return 0;
  if (isTriple(dice) || isHearth(dice)) return 0;
  let mask = 0;
  if (temper >= 2) {
    for (let i = 0; i < 3; i++) {
      const j = (i + 1) % 3, k = (i + 2) % 3;
      if (dice[j] === dice[k] && dice[j]! >= 5 && dice[i] !== dice[j]) mask = 1 << i;
    }
  }
  if (!mask) for (let i = 0; i < 3; i++) if (dice[i]! <= 2 + temper) mask |= 1 << i;
  if (!mask) return 0;
  const score = handValue(dice);
  const behind = score < Math.max(10, bestStood) || score < 11 + 2 * temper;
  if (!behind || popcount(mask) > 1 + temper) return 0;
  return mask;
}

// ---- seeded randomness ----------------------------------------------------------------------------
/** mulberry32: a small seeded generator (uniform in [0, 1)). */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable 32-bit hash of a few integers (the throw's motion, a sound's variant). */
export function hashInts(...xs: number[]): number {
  let h = 0x811c9dc5;
  for (const x of xs) { h ^= x | 0; h = Math.imul(h, 0x01000193); h ^= h >>> 13; }
  return h >>> 0;
}

// ---- the regulars ---------------------------------------------------------------------------------
export interface Regular { name: string; frame: number; temper: 0 | 1 | 2 }
/** The inn's regulars: villagers with their own coin, Oryx folk frames 38–50, 75–77, 86. */
export const REGULARS: readonly Regular[] = [
  { name: 'Old Hesk', frame: 75, temper: 0 },
  { name: 'Merry Tolliver', frame: 40, temper: 2 },
  { name: 'One-Eyed Jory', frame: 38, temper: 2 },
  { name: 'Widow Pell', frame: 76, temper: 0 },
  { name: 'Barnaby Crook', frame: 41, temper: 1 },
  { name: 'Fat Odo', frame: 43, temper: 1 },
  { name: 'Sergeant Rusk', frame: 49, temper: 2 },
  { name: 'Nan Ebbit', frame: 50, temper: 0 },
  { name: 'Tobin the Drover', frame: 39, temper: 1 },
  { name: 'Brother Coll', frame: 44, temper: 0 },
  { name: 'Ysa Copperpot', frame: 77, temper: 1 },
  { name: 'Grell Butcher', frame: 42, temper: 2 },
  { name: 'Dame Hollis', frame: 86, temper: 1 },
];
export const TEMPER_WORD = ['cautious', 'steady', 'bold'] as const;

// ---- the local table ------------------------------------------------------------------------------
export interface LocalHero { id: bigint; owner: string; name: string; gold: number; frame?: number; classId?: string }
export interface LocalDiceOptions {
  /** Server ms (default: a clock that only moves when `advance` is called, from 1_000_000). */
  now?: () => number;
  seed?: number;
  /** The player `actions` act for, and whose seats are `mine` in `view()`. */
  me?: string;
  heroes?: LocalHero[];
  regulars?: readonly Regular[];
  /** The faces thrown, one per die (tests); default the seeded generator. */
  face?: () => number;
}

interface Seat {
  kind: 'empty' | 'hero' | 'regular';
  state: DiceSeatView['state'];
  name: string;
  frame: number;
  classId?: string;
  heroId?: bigint;
  owner?: string;
  temper?: number;
  score: number;
  hand: Dice3;
  won?: number;
}

export interface LocalDiceTable {
  view(viewer?: string): DiceView;
  /** The actions of `opts.me`. */
  actions: DiceActions;
  actionsFor(owner: string): DiceActions;
  subscribe(cb: () => void): () => void;
  addHero(h: LocalHero): void;
  hero(id: bigint): LocalHero | undefined;
  heroes(): LocalHero[];
  /** Moves the default clock (when no `now` was given). */
  advance(ms: number): void;
  now(): number;
}

const emptySeat = (): Seat => ({ kind: 'empty', state: 'empty', name: '', frame: 0, score: 0, hand: [0, 0, 0] });

export function createLocalDiceTable(opts: LocalDiceOptions = {}): LocalDiceTable {
  let clock = 1_000_000;
  const now = opts.now ?? (() => clock);
  const rand = seeded(opts.seed ?? 1);
  const die = opts.face ?? (() => 1 + Math.floor(rand() * 6));
  const roster = opts.regulars ?? REGULARS;
  const heroes = new Map<bigint, LocalHero>();
  for (const h of opts.heroes ?? []) heroes.set(h.id, { ...h });
  const subs = new Set<() => void>();

  const t = {
    state: 'open' as DiceView['state'], stake: 0, pot: 0, kitty: 0,
    seats: [emptySeat(), emptySeat(), emptySeat(), emptySeat()],
    turn: -1, dice: [0, 0, 0] as Dice3, thrown: 0, throwN: 0, pushes: 0,
    last: 'none' as DiceLast, deadlineMs: 0, gameN: 0, winners: [] as number[],
  };

  const seatAt = (i: number): Seat => t.seats[i]!;
  const emit = () => { for (const cb of [...subs]) cb(); };
  const heroSeated = () => t.seats.some((s) => s.kind === 'hero');
  const occupied = () => t.seats.filter((s) => s.kind !== 'empty').length;
  const dropRegulars = () => { t.seats = t.seats.map((s) => (s.kind === 'regular' ? emptySeat() : s)); };
  const bestStood = () => t.seats.reduce((m, s) => (s.state === 'stood' ? Math.max(m, s.score) : m), 0);
  const regularDelay = () => 1_600 + Math.floor(rand() * 600);

  function beginTurn(i: number) {
    t.turn = i;
    const s = seatAt(i);
    s.state = 'playing';
    t.dice = [0, 0, 0];
    t.thrown = 0;
    t.pushes = MAX_PUSHES;
    t.deadlineMs = now() + (s.kind === 'regular' ? REGULAR_FIRST_MS : TURN_MS);
  }

  function endTurn(state: 'stood' | 'bust', last: DiceLast) {
    const s = seatAt(t.turn);
    s.state = state;
    s.hand = [...t.dice] as Dice3;
    s.score = state === 'stood' ? handValue(t.dice) : 0;
    t.last = last;
    t.deadlineMs = now() + PAUSE_MS;
  }

  function standHand(last?: DiceLast) {
    const k = handKind(t.dice);
    endTurn('stood', last ?? (k === 'plain' ? 'stood' : k));
  }

  function throwDice(mask: number): void {
    t.throwN++;
    t.thrown = mask;
    for (let i = 0; i < 3; i++) if (mask & (1 << i)) t.dice[i] = die();
  }

  function doRoll(): void {
    throwDice(0b111);
    if (isGallows(t.dice)) { endTurn('bust', 'gallows'); return; }
    t.last = 'thrown';
    t.deadlineMs = now() + (seatAt(t.turn).kind === 'regular' ? regularDelay() : TURN_MS);
  }

  function doPush(mask: number): void {
    throwDice(mask);
    t.pushes--;
    for (let i = 0; i < 3; i++) if (mask & (1 << i) && t.dice[i] === SKULL) { endTurn('bust', 'bust'); return; }
    if (t.pushes === 0) { standHand(); return; }
    t.last = 'pushed';
    t.deadlineMs = now() + (seatAt(t.turn).kind === 'regular' ? regularDelay() : TURN_MS);
  }

  function settle() {
    t.turn = -1;
    t.thrown = 0;
    const stood = t.seats.map((s, i) => ({ s, i })).filter(({ s }) => s.state === 'stood');
    const top = stood.reduce((m, { s }) => Math.max(m, s.score), -1);
    const winners = stood.filter(({ s }) => s.score === top).map(({ i }) => i);
    t.winners = winners;
    if (winners.length) {
      const share = Math.floor(t.pot / winners.length);
      const odd = t.pot - share * winners.length;
      winners.forEach((i, n) => {
        const s = seatAt(i);
        s.won = share + (n === 0 ? odd : 0);
        if (s.kind === 'hero' && s.heroId !== undefined) { const h = heroes.get(s.heroId); if (h) h.gold += s.won; }
      });
      t.last = 'won';
    } else {
      t.kitty = t.pot;
      t.last = 'kitty';
    }
    t.pot = 0;
    t.state = 'settling';
    t.deadlineMs = now() + SETTLE_MS;
  }

  function advanceTurn() {
    for (let k = 1; k <= 4; k++) {
      const i = t.turn + k;
      if (i < 4 && seatAt(i).state === 'waiting') { beginTurn(i); return; }
    }
    settle();
  }

  function reopen() {
    t.state = 'open';
    t.turn = -1;
    t.deadlineMs = 0;
    t.thrown = 0;
    t.seats = t.seats.map((s) => (s.kind === 'empty' ? emptySeat() : { ...s, state: 'waiting', score: 0, hand: [0, 0, 0], won: undefined }));
    if (!heroSeated()) { dropRegulars(); t.stake = 0; }
  }

  function regularAct() {
    const s = seatAt(t.turn);
    if (t.dice.every((d) => d === 0)) { doRoll(); return; }
    const mask = regularChoice(t.dice, t.pushes, s.temper ?? 1, bestStood());
    if (mask) doPush(mask);
    else standHand();
  }

  function tick() {
    const at = now();
    if (t.state === 'open' || at < t.deadlineMs) return;
    if (t.state === 'settling') { reopen(); emit(); return; }
    const s = seatAt(t.turn);
    if (!s || s.state !== 'playing') { advanceTurn(); emit(); return; }
    if (s.kind === 'regular') { regularAct(); emit(); return; }
    // a hero's 30 s ran out
    if (t.dice.every((d) => d === 0)) {
      throwDice(0b111);
      if (isGallows(t.dice)) endTurn('bust', 'gallows');
      else standHand('timeout');
    } else standHand('timeout');
    emit();
  }

  const mySeat = (owner: string) => t.seats.findIndex((s) => s.kind === 'hero' && s.owner === owner);
  const myTurn = (owner: string) => t.state === 'playing' && t.turn >= 0 && seatAt(t.turn).kind === 'hero' && seatAt(t.turn).owner === owner && seatAt(t.turn).state === 'playing';

  function actionsFor(owner: string): DiceActions {
    const done = (ok: boolean) => { if (ok) emit(); return Promise.resolve(ok); };
    return {
      sit(heroId) {
        const h = heroes.get(heroId);
        if (t.state !== 'open' || !h || h.owner !== owner) return done(false);
        let i = mySeat(owner);
        if (i < 0) i = t.seats.findIndex((s) => s.kind === 'empty');
        if (i < 0) i = t.seats.findIndex((s) => s.kind === 'regular');
        if (i < 0) return done(false);
        t.seats[i] = { kind: 'hero', state: 'waiting', name: h.name, frame: h.frame ?? 0, ...(h.classId ? { classId: h.classId } : {}), heroId: h.id, owner, score: 0, hand: [0, 0, 0] };
        if (!t.stake) t.stake = 1;
        t.last = 'sat';
        return done(true);
      },
      stand() {
        const i = mySeat(owner);
        if (i < 0) return done(false);
        if (t.state === 'playing') {
          const wasTurn = t.turn === i && seatAt(i).state === 'playing';
          t.seats[i] = { ...emptySeat(), state: 'out' };
          t.last = 'left';
          if (wasTurn) t.deadlineMs = now() + PAUSE_MS;
          return done(true);
        }
        t.seats[i] = emptySeat();
        t.last = 'left';
        if (!heroSeated()) { dropRegulars(); if (t.state === 'open') t.stake = 0; }
        return done(true);
      },
      start(stake) {
        if (t.state !== 'open' || mySeat(owner) < 0 || !(DICE_STAKES as readonly number[]).includes(stake)) return done(false);
        // regulars fill the empty stools until three are taken
        const sitting = new Set(t.seats.filter((s) => s.kind === 'regular').map((s) => s.name));
        const free = roster.filter((r) => !sitting.has(r.name));
        while (occupied() < 3 && free.length) {
          const r = free.splice(Math.floor(rand() * free.length), 1)[0]!;
          const i = t.seats.findIndex((s) => s.kind === 'empty');
          t.seats[i] = { kind: 'regular', state: 'waiting', name: r.name, frame: r.frame, temper: r.temper, score: 0, hand: [0, 0, 0] };
        }
        // every hero pays; one who cannot is stood up
        for (let i = 0; i < 4; i++) {
          const s = seatAt(i);
          if (s.kind !== 'hero' || s.heroId === undefined) continue;
          const h = heroes.get(s.heroId);
          if (!h || h.gold < stake) { t.seats[i] = emptySeat(); continue; }
          h.gold -= stake;
        }
        if (!heroSeated()) { dropRegulars(); t.stake = 0; return done(false); }
        t.stake = stake;
        t.pot = t.kitty + stake * occupied();
        t.kitty = 0;
        t.winners = [];
        for (const s of t.seats) if (s.kind !== 'empty') { s.state = 'waiting'; s.score = 0; s.hand = [0, 0, 0]; s.won = undefined; } else s.state = 'empty';
        t.state = 'playing';
        t.gameN++;
        beginTurn(t.seats.findIndex((s) => s.kind !== 'empty'));
        t.last = 'started';
        return done(true);
      },
      roll() {
        if (!myTurn(owner) || t.dice.some((d) => d !== 0)) return done(false);
        doRoll();
        return done(true);
      },
      push(mask) {
        if (!myTurn(owner) || t.dice.some((d) => d === 0) || t.pushes <= 0 || mask <= 0 || mask > 0b111) return done(false);
        doPush(mask);
        return done(true);
      },
      hold() {
        if (!myTurn(owner) || t.dice.some((d) => d === 0)) return done(false);
        standHand();
        return done(true);
      },
      tick,
    };
  }

  function view(viewer = opts.me): DiceView {
    return {
      state: t.state, stake: t.stake, pot: t.pot, kitty: t.kitty,
      seats: t.seats.map((s) => ({
        kind: s.kind, state: s.state, name: s.name, frame: s.frame,
        ...(s.classId ? { classId: s.classId } : {}),
        ...(s.heroId !== undefined ? { heroId: s.heroId } : {}),
        ...(s.owner ? { owner: s.owner } : {}),
        mine: s.kind === 'hero' && !!viewer && s.owner === viewer,
        score: s.score, hand: [...s.hand] as Dice3,
        ...(s.temper !== undefined ? { temper: s.temper } : {}),
        ...(s.won !== undefined ? { won: s.won } : {}),
      })),
      turn: t.turn, dice: [...t.dice] as Dice3, thrown: t.thrown, throwN: t.throwN, pushes: t.pushes,
      last: t.last, deadlineMs: t.deadlineMs, gameN: t.gameN, winners: [...t.winners],
    };
  }

  return {
    view,
    actions: actionsFor(opts.me ?? 'me'),
    actionsFor,
    subscribe(cb) { subs.add(cb); return () => { subs.delete(cb); }; },
    addHero(h) { heroes.set(h.id, { ...h }); },
    hero: (id) => heroes.get(id),
    heroes: () => [...heroes.values()],
    advance(ms) { clock += ms; },
    now,
  };
}
